import {
    TextInlineEditor,
    TextInlineEditorProps,
} from 'components/inline_edit/TextInlineEditor';
import { NoValue } from 'components/no_value/NoValue';
import {
    IntegrationAuthMaxChar,
    IntegrationMaxChar,
} from 'pages/Settings/Integrations/shared';
import { DetailRow } from 'pages/Settings/Integrations/tabs/IntegrationSettingsTab';
import { ReactNode } from 'react';
import { pluralize } from 'utils/string';
import { isUrlValid } from 'utils/validation';

const renderMaskedValue = (value: string | undefined) =>
    value ? '*'.repeat(value.length) : <NoValue />;

const checkLength = (
    value: string | undefined,
    maxLength: number,
    minLength?: number,
) => {
    if (value && value.length > maxLength) {
        return `Maximum ${maxLength} characters allowed`;
    }
    if (minLength && (!value || value.length < minLength)) {
        return `Minimum ${minLength} ${pluralize(minLength, 'character')} required`;
    }
    return undefined;
};

const validateAuth = (value: string | undefined, canClear?: boolean) =>
    checkLength(value, IntegrationAuthMaxChar, canClear ? 0 : 1);

export const validateUrl = (value: string | undefined) => {
    if (value && !isUrlValid(value)) {
        return 'Invalid URL';
    }
    return checkLength(value, IntegrationMaxChar);
};

export type IntegrationDetailsInlineEditorProps = Omit<
    TextInlineEditorProps,
    'render' | 'validate' | 'type'
> &
    Readonly<{
        name: string | ReactNode;
        type: 'url' | 'secret';
        canClear?: boolean;
    }>;

export const IntegrationDetailsInlineEditor = ({
    name,
    type,
    canClear,
    ...props
}: IntegrationDetailsInlineEditorProps) => (
    <DetailRow name={name}>
        <TextInlineEditor
            renderValue={type === 'secret' ? renderMaskedValue : (v) => v || <NoValue />}
            validate={(v) =>
                type === 'secret' ? validateAuth(v, canClear) : validateUrl(v)
            }
            valueClass="ellipsis-1-line flex-1 mr-5"
            type={type === 'secret' ? 'password' : undefined}
            {...props}
        />
    </DetailRow>
);
